import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import T from "@/lib/testIds";

/**
 * Brand logo component.
 * Auto-fetches custom logo/brand from public settings.
 *
 * Click behavior:
 * 1–4 taps within 3s → trigger feed refresh (onRefresh callback)
 * 5 taps within 3s  → hidden admin login (/admin/login)
 */
export default function Logo({ compact = false, light = false, onRefresh }) {
  const nav = useNavigate();
  const tapState = useRef({ count: 0, timer: null });
  const [meta, setMeta] = useState({ logo_url: "/logo.png", brand_name: "Zunayya_nitipdong" });
  const [pulse, setPulse] = useState(false);

  useEffect(() => {
    api.get("/settings/public").then((r) => {
      setMeta({
        logo_url: r.data?.logo_url || "/logo.png",
        brand_name: r.data?.brand_name || "Zunayya_nitipdong"
      });
    }).catch(() => {});
  }, []);

  useEffect(() => () => {
    if (tapState.current.timer) clearTimeout(tapState.current.timer);
  }, []);

  const onTap = () => {
    tapState.current.count += 1;

    if (tapState.current.timer) clearTimeout(tapState.current.timer);

    // Visual pulse on every tap
    setPulse(true);
    setTimeout(() => setPulse(false), 300);

    if (tapState.current.count >= 5) {
      // Hidden admin access
      tapState.current.count = 0;
      nav("/admin/login");
      return;
    }

    // Reset counter after 3s of inactivity
    tapState.current.timer = setTimeout(() => {
      // If 1-4 taps happened, trigger refresh
      if (tapState.current.count >= 1 && tapState.current.count < 5 && onRefresh) {
        onRefresh();
      }
      tapState.current.count = 0;
    }, 600);
  };

  return (
    <div
      data-testid={T.logo}
      className={`group flex shrink-0 items-center gap-2.5 select-none whitespace-nowrap cursor-pointer ${pulse ? "scale-95" : "scale-100"} transition-transform duration-200`}
      aria-label={`${meta.brand_name} logo`}
      role="img"
      onClick={onTap}
    >
      {meta.logo_url ? (
        <span className="relative inline-flex items-center justify-center h-10 w-10 rounded-2xl overflow-hidden bg-white/70 dark:bg-plum/40 border border-plum/15 group-active:scale-95 transition-transform">
          <img src={meta.logo_url || "/logo.png"} alt="" className="h-full w-full object-cover" draggable={false} onError={(e) => { e.target.style.display = 'none'; }} />
        </span>
      ) : (
        <span className="relative inline-flex items-center justify-center h-10 w-10 rounded-2xl bg-sakura-200 text-plum font-serif italic text-xl border border-plum/20 group-active:scale-95 transition-transform">
          {(meta.brand_name || "Zn").replace(/[^A-Za-z]/g, "").slice(0, 2).toLowerCase() || "zn"}
          <Sparkles className="absolute -top-1.5 -right-1.5 h-3 w-3 text-buttercream-600 fill-buttercream-500 animate-twinkle" />
        </span>
      )}
      {!compact && (
        <span className="hidden md:flex flex-col leading-none">
          <span className={`font-serif italic text-2xl tracking-tight ${light ? "text-white" : "text-plum dark:text-cream"}`}>
            {meta.brand_name}
          </span>
          <span className={`text-[10px] font-medium tracking-[0.25em] uppercase mt-1 ${light ? "text-white/80" : "text-plum/60 dark:text-cream/60"}`}>
            Thailand
          </span>
        </span>
      )}
    </div>
  );
}
